import { createResponderEventSource } from 'features/Responder/utils/createResponderEventSource';
import Cookies from 'js-cookie';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import apiClient from 'services/_infra/apiClient';
import { getScheduleColorMap } from 'utils/recurrenceUtils';

export const SchedulesContext = createContext();

const resolveRunUpdatePayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const direct =
    payload?.data?.run ??
    payload?.run ??
    payload?.data?.payload?.run ??
    (payload?.data && payload.data.id != null ? payload.data : null) ??
    (payload?.id != null ? payload : null);
  if (Array.isArray(direct)) return direct.filter(Boolean);
  return direct && typeof direct === 'object' ? direct : null;
};

export const SchedulesProvider = ({ children }) => {
  const [schedules, setSchedules] = useState([]);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [activeEditSchedule, setActiveEditSchedule] = useState(null);
  const [activeRunId, setActiveRunId] = useState(null);
  const [runs, setRuns] = useState([]);
  const token = Cookies.get('accessToken');
  const colorMap = getScheduleColorMap();

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await apiClient.get(`/responder/schedules/definitions`);
      if (res?.data?.definitions) {
        setSchedules(res.data.definitions);
        setLastUpdateTime(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch schedules', err);
    }
  }, []);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await apiClient.get(`/responder/schedules/runs`);
      if (res?.data?.runs) {
        setRuns(res.data.runs);
        setLastUpdateTime(new Date());
      }
    } catch (err) {
      console.error('Failed to fetch runs', err);
    }
  }, []);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Delete schedule by id
  const deleteSchedule = useCallback(
    async (ids) => {
      if (!ids) return;
      try {
        const idList = Array.isArray(ids) ? ids.join(',') : ids;
        console.log('Deleting schedules with IDs:', idList);
        const res = await apiClient.delete(`/responder/schedules/definitions/?ids=${idList}`);
        console.log('Delete response:', res);
        fetchSchedules();
      } catch (err) {
        console.error('Failed to delete schedule', err);
        alert('Failed to delete schedule.');
      }
    },
    [fetchSchedules],
  );

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const sse = createResponderEventSource({
      path: '/responder/notifier_Event',
      eventListeners: {
        'run-update': (event) => {
          try {
            const parsed = JSON.parse(event.data);
            const runPayload = parsed?.data?.run ?? parsed?.run ?? parsed?.data ?? null;
            if (Array.isArray(runPayload)) {
              runPayload.forEach((run) => updateRunsInMemory(run));
              setLastUpdateTime(new Date());
              return;
            }
            if (runPayload && typeof runPayload === 'object') {
              updateRunsInMemory(runPayload);
              setLastUpdateTime(new Date());
            }
          } catch (err) {
            console.error('Failed to parse run SSE payload', err);
          }
        },
      },
      onOpen: () => {
        console.log('Run SSE connection opened');
      },
      onError: (err) => {
        console.error('Run SSE error', err);
      },
    });

    return () => {
      sse.close();
    };
  }, [token]);

  const updateRunsInMemory = (data) => {
    if (!data) return;
    setRuns((prev) => {
      const updated = [...prev];
      const index = updated.findIndex((run) => run.id === data.id);
      if (index !== -1) {
        updated[index] = { ...updated[index], ...data };
      } else {
        updated.push(data);
      }
      return updated;
    });
  };

  const contextValue = {
    schedules,
    setSchedules,
    fetchSchedules,
    lastUpdateTime,
    activeEditSchedule,
    setActiveEditSchedule,
    activeRunId,
    setActiveRunId,
    deleteSchedule,
    runs,
    setRuns,
    colorMap,
  };

  return <SchedulesContext.Provider value={contextValue}>{children}</SchedulesContext.Provider>;
};

export const useSchedules = () => {
  const context = useContext(SchedulesContext);
  if (!context) {
    throw new Error('useSchedules must be used within a SchedulesProvider');
  }
  return context;
};
